import type {
  AddressCandidate,
  LoadListSource,
} from "../../../../../_shared/_logic/batch-upload-contract"
import { normalizeKey } from "../address-import"

export type ApplyBacklogPropertySelectionInput = {
  localProperties: Property[]
  selectedPropertyIds: string[]
  addressCandidates: AddressCandidate[]
  backlogProperties: Property[]
  backlogCandidates: AddressCandidate[]
}

type ApplyImportedPropertySelectionInput = {
  localProperties: Property[]
  selectedPropertyIds: string[]
  addressCandidates: AddressCandidate[]
  source: Exclude<LoadListSource, "direct">
  importedProperties: Property[]
  importedCandidates: AddressCandidate[]
}

export type SourceSelectionResult = {
  localProperties: Property[]
  selectedPropertyIds: string[]
  addressCandidates: AddressCandidate[]
  addedCount: number
  removedPropertyIds: string[]
}

const knownSources = new Set<LoadListSource>(["direct", "backlog_pdf"])

const uniqueStrings = (values: Array<string | null | undefined>) =>
  Array.from(new Set(values.map((value) => `${value ?? ""}`).filter(Boolean)))

export const normalizeLoadListSources = (
  sources: LoadListSource[] | undefined,
  fallback: LoadListSource[] = [],
): LoadListSource[] => {
  const normalized = (sources?.length ? sources : fallback).filter(
    (source): source is LoadListSource => knownSources.has(source),
  )

  return Array.from(new Set(normalized))
}

export const candidateHasSource = (candidate: AddressCandidate, source: LoadListSource) =>
  normalizeLoadListSources(candidate.sources).includes(source)

export const candidateFromProperty = (
  property: Property,
  sources: LoadListSource[] = ["direct"],
): AddressCandidate => ({
  key: normalizeKey(property),
  propertyId: property.id,
  sources: normalizeLoadListSources(sources),
  payload: {
    street: property.street,
    house_number: property.house_number ?? null,
    zip: property.zip ?? null,
    city: property.city ?? null,
    country: property.country ?? null,
    address_extra: property.address_extra ?? null,
  },
})

export const normalizeAddressCandidateSources = (
  candidate: AddressCandidate,
  fallback: LoadListSource[] = [],
): AddressCandidate => ({
  ...candidate,
  sources: normalizeLoadListSources(candidate.sources, fallback),
})

export const mergePropertiesById = (base: Property[], incoming: Property[]) => {
  const byId = new Map<string, Property>()
  base.forEach((property) => property?.id && byId.set(property.id, property))
  incoming.forEach((property) => property?.id && byId.set(property.id, property))

  return Array.from(byId.values())
}

export const selectedPropertyIdsFromCandidates = (candidates: AddressCandidate[]) =>
  uniqueStrings(candidates.map((candidate) => candidate.propertyId))

export const selectionSourcesByPropertyIdFromCandidates = (
  candidates: AddressCandidate[],
): Record<string, LoadListSource[]> => {
  const byPropertyId = new Map<string, LoadListSource[]>()
  candidates.forEach((candidate) => {
    if (!candidate.propertyId) return

    const current = byPropertyId.get(candidate.propertyId) ?? []
    byPropertyId.set(
      candidate.propertyId,
      normalizeLoadListSources([...current, ...normalizeLoadListSources(candidate.sources)]),
    )
  })

  return Object.fromEntries(byPropertyId)
}

const candidateIdentity = (candidate: AddressCandidate) =>
  candidate.propertyId ? `property:${candidate.propertyId}` : `address:${candidate.key}`

/** Merge in first-occurrence order without conflating unresolved addresses with known properties. */
const mergeCandidates = (existing: AddressCandidate[], incoming: AddressCandidate[]) => {
  const candidates = [...existing]
  const indexByIdentity = new Map<string, number>()
  existing.forEach((candidate, index) => {
    const identity = candidateIdentity(candidate)

    if (!indexByIdentity.has(identity)) indexByIdentity.set(identity, index)
  })
  incoming.forEach((candidate) => {
    const identity = candidateIdentity(candidate)
    const index = indexByIdentity.get(identity)

    if (index === undefined) {
      indexByIdentity.set(identity, candidates.length)
      candidates.push(candidate)

      return
    }

    const current = candidates[index]
    candidates[index] = {
      ...current,
      ...candidate,
      sources: normalizeLoadListSources([
        ...normalizeLoadListSources(current.sources),
        ...normalizeLoadListSources(candidate.sources),
      ]),
    }
  })

  return candidates
}

const withoutSource = (candidates: AddressCandidate[], source: LoadListSource) =>
  candidates
    .map((candidate) => {
      const withoutSelectedSource = {
        ...candidate,
        sources: normalizeLoadListSources(candidate.sources).filter((entry) => entry !== source),
      }

      return source === "backlog_pdf"
        ? { ...withoutSelectedSource, backlogResolution: undefined }
        : withoutSelectedSource
    })
    .filter((candidate) => candidate.sources.length > 0)

const removedPropertyIds = (previous: string[], next: string[]) => {
  const nextIds = new Set(next)

  return previous.filter((propertyId) => !nextIds.has(propertyId))
}

const applyImportedPropertySelectionToLoadList = ({
  localProperties,
  selectedPropertyIds,
  addressCandidates,
  source,
  importedProperties,
  importedCandidates,
}: ApplyImportedPropertySelectionInput): SourceSelectionResult => {
  const previousIds = uniqueStrings(selectedPropertyIds)
  const normalizedExisting = addressCandidates.map((candidate) =>
    normalizeAddressCandidateSources(candidate, ["direct"]),
  )
  const nextCandidates = mergeCandidates(
    withoutSource(normalizedExisting, source),
    importedCandidates.map((candidate) => normalizeAddressCandidateSources(candidate, [source])),
  )

  const nextSelectedIds = selectedPropertyIdsFromCandidates(nextCandidates)
  const previousIdSet = new Set(previousIds)
  const addedCount = nextSelectedIds.filter((propertyId) => !previousIdSet.has(propertyId)).length

  return {
    localProperties: mergePropertiesById(localProperties, importedProperties),
    selectedPropertyIds: nextSelectedIds,
    addressCandidates: nextCandidates,
    addedCount,
    removedPropertyIds: removedPropertyIds(previousIds, nextSelectedIds),
  }
}

export const applyBacklogPropertySelectionToLoadList = ({
  localProperties,
  selectedPropertyIds,
  addressCandidates,
  backlogProperties,
  backlogCandidates,
}: ApplyBacklogPropertySelectionInput): SourceSelectionResult =>
  applyImportedPropertySelectionToLoadList({
    localProperties,
    selectedPropertyIds,
    addressCandidates,
    source: "backlog_pdf",
    importedProperties: backlogProperties,
    importedCandidates: backlogCandidates,
  })
